import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { ConfigActivityRow } from "@/api/config-server-stages";
import { conversationRefusalText } from "@/lib/conversation-refusal";
import { GrowConnectionDialog } from "./ConnectionDialogs";
import type { ConfigActivityEntry } from "./activityEntries";
import {
  ExecutionListCard,
  ExecutionLine,
  type ExecutionListRow,
  type ExecutionRowStatus,
} from "./ExecutionListCard";
import { LoadMore } from "./Paging";
const STATUS: Record<string, ExecutionRowStatus> = {
  running: "running",
  awaiting_human_decision: "waiting",
  awaiting_human: "waiting",
  completed: "done",
  failed: "failed",
  interrupted: "interrupted",
};

export interface ConfigActivityListProps {
  rows: ConfigActivityRow[];
  onDetails: (entry: ConfigActivityEntry) => void;
  title?: string;
  onRun?: (code: string) => void;
  runDisabledReason?: string | null;
  activeCode?: string | null;
  onSelect?: (code: string) => void;
  conversationCode?: string | null;
  showLaunchLink?: boolean;
  growConfigured?: boolean;
  growUrl?: string | null;
  projectId?: string;
  onConfigureGrow?: () => void;
  toolbarActions?: ReactNode;
  notice?: ReactNode;
  search?: string;
  onSearchChange?: (q: string) => void;
  onSearchEnter?: () => void;
  done?: number;
  total?: number;
  body?: ReactNode;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
}
export function ConfigActivityList(props: ConfigActivityListProps) {
  const { t } = useTranslation();
  const [growDialogOpen, setGrowDialogOpen] = useState(false);
  useEffect(() => {
    if (growDialogOpen && props.growConfigured) setGrowDialogOpen(false);
  }, [growDialogOpen, props.growConfigured]);
  return (
    <>
      <ExecutionListCard
        overline={t("APPROVED IN MANAGEMENT")}
        title={props.title ?? t("SSCUI configurations")}
        done={props.done ?? 0}
        total={props.total ?? 0}
        connection={
          props.growConfigured === undefined
            ? null
            : props.growConfigured
              ? {
                  tone: "ok",
                  label: t("Configured for GROW"),
                  url: props.growUrl,
                  openLabel: t("Open GROW in a new tab"),
                }
              : {
                  tone: "warn",
                  label: t("GROW not configured"),
                  configureLabel: t("Configure the GROW connection"),
                  onConfigure:
                    props.onConfigureGrow ??
                    (props.projectId
                      ? () => setGrowDialogOpen(true)
                      : undefined),
                }
        }
        search={props.search ?? ""}
        onSearchChange={props.onSearchChange ?? (() => {})}
        onSearchEnter={props.onSearchEnter}
        toolbarActions={props.toolbarActions}
        notice={props.notice}
        selectedKey={props.activeCode}
        onSelect={(code) => props.onSelect?.(code)}
        rows={props.rows.map((entry) => activityRow(entry, props, t))}
        body={props.body}
      />
      {props.onLoadMore && (
        <LoadMore
          hasMore={!!props.hasMore}
          loading={!!props.loadingMore}
          onLoadMore={props.onLoadMore}
        />
      )}
      {props.projectId && !props.onConfigureGrow && (
        <GrowConnectionDialog
          projectId={props.projectId}
          open={growDialogOpen}
          onOpenChange={setGrowDialogOpen}
        />
      )}
    </>
  );
}
export function ActivityExecutionRows(props: ConfigActivityListProps) {
  const { t } = useTranslation();
  return (
    <ul>
      {props.rows.map((entry) => (
        <ExecutionLine
          key={entry.activity_code}
          row={activityRow(entry, props, t)}
          selected={entry.activity_code === props.activeCode}
          onSelect={(code) => props.onSelect?.(code)}
        />
      ))}
    </ul>
  );
}
function activityRow(
  entry: ConfigActivityEntry,
  {
    onDetails,
    onRun,
    runDisabledReason,
    conversationCode,
    showLaunchLink,
  }: ConfigActivityListProps,
  t: ReturnType<typeof useTranslation>["t"],
): ExecutionListRow {
  const code = entry.activity_code;
  const executed = entry.status === "completed";
  // A linha que segura a conversa do projeto mostra o giro no lugar do ▶; as outras seguem travadas.
  const holdsConversation = !!conversationCode && conversationCode === code;
  const running = entry.status === "running" || holdsConversation;
  // Sem link a SSCUI não abre: o backend recusa a conversa sem `img_activity`.
  const linkUnavailable =
    showLaunchLink && !entry.launch_url
      ? conversationRefusalText(
          t,
          entry.launch_unavailable_reason ?? "no_img_activity",
          "sscui",
        )
      : null;
  // Sem extração não há o que cadastrar: o ▶ e os detalhes ficam travados (spec 2026-10-29).
  const notExtracted = entry.extracted === false;
  const runBlocked =
    runDisabledReason ??
    (notExtracted ? t("No extraction: extract before running") : null) ??
    linkUnavailable;
  const status: ExecutionRowStatus = holdsConversation
    ? "running"
    : (STATUS[entry.status] ?? "idle");
  // "Not extracted" só no lugar de "Not executed": com execução anterior vale o status dela (D-A15).
  const statusLabel = holdsConversation
    ? t("In progress")
    : notExtracted && status === "idle" && !entry.last_run_id
      ? t("Not extracted")
      : undefined;
  return {
    key: code,
    code,
    name: entry.name ?? code,
    status,
    statusLabel,
    testId: `activity-${code}`,
    launch: showLaunchLink
      ? {
          url: entry.launch_url ?? null,
          label: t("Open {{code}} in SAP", { code }),
          disabledReason: linkUnavailable,
        }
      : undefined,
    run:
      onRun && !executed
        ? {
            label: t("Run {{code}}", { code }),
            title: running ? t("In progress") : (runBlocked ?? t("Run")),
            disabled: !!runBlocked,
            busy: running,
            onRun: () => onRun(code),
          }
        : null,
    details: {
      label: t("Details of {{code}}", { code }),
      onDetails: () => onDetails(entry),
      disabledReason: notExtracted ? t("No extraction to show") : null,
    },
  };
}
